import React, { useState, useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { 
  addComponent, 
  updateComponentPosition, 
  selectComponents, 
  selectSelectedComponentId,
  selectLayoutInfo,
  selectComponent,
  selectMenus,
  assignComponentsToMenu,
  removeComponent
} from '../redux/editorSlice';
import ComponentRenderer from './ComponentRenderer';
import { getLayoutComponentById } from '../layouts';
import { COMPONENT_TYPES } from '../constants';

function EditorCanvas({ menuPath }) {
  const dispatch = useDispatch();
  const components = useSelector(selectComponents);
  const layoutInfo = useSelector(selectLayoutInfo);
  const selectedComponentId = useSelector(selectSelectedComponentId);
  const { selectedLayout, layoutProps } = layoutInfo;
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragPosition, setDragPosition] = useState(null); // 'above', 'below', 'left', 'right'
  const [_isScrolling, setIsScrolling] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(null); // 'up', 'down'
  const [isDraggingComponent, setIsDraggingComponent] = useState(false);
  // 화면 크기 모드 상태 추가
  const [viewMode, setViewMode] = useState('pc'); // 'pc', 'tablet', 'mobile'
  // 현재 활성화된 메뉴 상태 추가
  const [activeMenu, setActiveMenu] = useState(null);
  // 토스트 메시지 상태 추가
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toastTimeoutRef = useRef(null);
  const menus = useSelector(selectMenus);
  
  // 디바이스 프레임 스타일
  const deviceFrameStyles = {
    pc: {
      border: 'none',
      borderRadius: '0',
      boxShadow: 'none'
    },
    tablet: {
      border: '16px solid #333',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      overflow: 'auto', // hidden에서 auto로 변경
      height: 'auto', // 고정 높이에서 auto로 변경
      minHeight: '1180px' // 최소 높이는 유지
    },
    mobile: {
      border: '12px solid #333',
      borderTopWidth: '40px', 
      borderBottomWidth: '40px',
      borderRadius: '24px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      position: 'relative',
      overflow: 'auto', // hidden에서 auto로 변경
      height: 'auto', // 고정 높이에서 auto로 변경
      minHeight: '844px' // 최소 높이는 유지
    }
  };

  // 화면 크기 설정
  const viewSizes = {
    pc: { width: '100%', maxWidth: '100%' },
    tablet: { width: '820px', maxWidth: '820px' }, // iPad Air 기준
    mobile: { width: '390px', maxWidth: '390px' }  // iPhone 12 Pro 기준
  };
  
  // 실제 화면 너비값 (미디어 쿼리 시뮬레이션용)
  const viewportWidths = {
    pc: 1200,
    tablet: 820, // iPad Air 기준
    mobile: 390  // iPhone 12 Pro 기준
  };

  // 메뉴 경로에 해당하는 메뉴를 찾아 활성화
  useEffect(() => {
    if (menuPath) {
      const matchedMenu = menus.find(menu => menu.path === menuPath);
      setActiveMenu(matchedMenu || null);
    } else {
      setActiveMenu(null);
    }
  }, [menuPath, menus]);

  // 메뉴 클릭 감지 및 PC 모드 자동 전환 기능 추가
  useEffect(() => {
    const handleMenuClick = (e) => {
      // 클릭된 요소가 메뉴 항목인지 확인
      const isMenuClick = e.target.closest('.menu-item a') || 
                         e.target.closest('.nav-menu-link') ||
                         e.target.closest('.mobile-navigation a') ||
                         e.target.closest('.offcanvas-mobile-menu a');
      
      // 모바일이나 태블릿 모드에서 메뉴를 클릭하면 PC 모드로 자동 전환
      if (isMenuClick && (viewMode === 'mobile' || viewMode === 'tablet')) {
        handleViewModeChange('pc');
      }
    };

    // 문서 전체에 클릭 이벤트 리스너 추가
    document.addEventListener('click', handleMenuClick);

    // 정리 함수
    return () => {
      document.removeEventListener('click', handleMenuClick);
    };
  }, [viewMode]);

  // 모바일/태블릿 모드에서 클릭 이벤트 제어 및 토스트 메시지 표시
  useEffect(() => {
    const handleLinkClick = (e) => {
      // 태블릿이나 모바일 모드인 경우에만 처리
      if (viewMode !== 'pc') {
        // 클릭된 요소가 프리뷰 콘텐츠 내부인지 확인 (레이아웃 컨테이너 또는 에디터 콘텐츠 내부)
        const isPreviewContent = e.target.closest('.layout-container') || 
                               e.target.closest('.layout-main-content') || 
                               e.target.closest('.editor-content');
                               
        // 에디터 UI 요소인지 확인 (에디터 툴바, 디바이스 버튼 등)
        const isEditorUI = e.target.closest('.editor-toolbar') || 
                           e.target.closest('.device-view-selector') || 
                           e.target.closest('.device-buttons') ||
                           e.target.closest('.editor-canvas-header') ||
                           e.target.closest('.property-panel') ||
                           e.target.closest('.component-library');
        
        // 프리뷰 콘텐츠 내부이면서 에디터 UI가 아닌 경우에만 처리
        if (isPreviewContent && !isEditorUI) {
          // 클릭 가능한 모든 요소 확인 (링크, 버튼, 제품 목록 등)
          const isInteractive = 
            e.target.closest('a') || 
            e.target.closest('button') || 
            e.target.closest('input[type="submit"]') || 
            e.target.closest('input[type="button"]') || 
            e.target.closest('.cart-wrap') ||
            e.target.closest('.hamburger-menu') || 
            e.target.closest('.mobile-menu-button') ||
            e.target.closest('.mobile-aside-button') ||
            e.target.closest('[role="button"]') ||
            e.target.closest('.clickable') ||
            e.target.closest('.nav-link') ||
            e.target.closest('.nav-item') ||
            e.target.closest('.product-grid') ||
            e.target.closest('.product-item') ||
            e.target.closest('.product-card') ||
            e.target.closest('.gi-pro-image') ||
            e.target.closest('.menu-item') ||
            e.target.closest('.product-img') ||
            e.target.closest('.slick-arrow') ||
            e.target.closest('.slick-dot') ||
            e.target.closest('.gi-btn-1') ||
            e.target.closest('.gi-btn-2') ||
            e.target.closest('.banner-item') ||
            e.target.closest('.banner-slider-item') ||
            e.target.closest('.banner-link') ||
            e.target.closest('.hover-link') ||
            e.target.closest('.flone-navigation') ||
            (e.target.tagName && ['A', 'BUTTON', 'INPUT', 'SELECT'].includes(e.target.tagName.toUpperCase()));
            
          if (isInteractive) {
            // 기본 동작 방지
            e.preventDefault();
            e.stopPropagation();
            
            // 토스트 메시지 표시
            showToastMessage('에디터 내에서 페이지 이동 및 버튼은 사용할 수 없습니다.');
            
            return false;
          }
        }
      }
    };
    
    // 문서 전체에 캡처링 단계에서 클릭 이벤트 리스너 추가
    document.addEventListener('click', handleLinkClick, true);
    
    // 정리 함수
    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [viewMode]);

  // 토스트 메시지 표시 함수
  const showToastMessage = (message) => {
    // 이전 타이머가 있으면 제거
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    // 토스트 메시지 설정 및 표시
    setToastMessage(message);
    setShowToast(true);
    
    // 3초 후 토스트 메시지 숨기기
    toastTimeoutRef.current = setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  // iframe에서 보내는 메시지 수신하여 토스트 메시지 표시
  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (event.data && event.data.type === 'SHOW_TOAST') {
        showToastMessage(event.data.message);
      }
    };

    window.addEventListener('message', handleIframeMessage);
    
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, []);
  
  // 스크롤 컨트롤을 위한 참조
  const canvasRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const scrollThreshold = 80; // 스크롤이 시작되는 경계 영역 크기(px)
  const scrollSpeed = 10; // 스크롤 속도 조절
  
  // iframe 참조 추가
  const iframeRef = useRef(null);

  // 선택된 레이아웃 컴포넌트 가져오기
  const LayoutComponent = selectedLayout ? getLayoutComponentById(selectedLayout) : null;
  
  // 전체 문서에 현재 디바이스 모드 클래스 적용
  useEffect(() => {
    const applyDeviceClass = () => {
      // 기존 클래스 제거
      document.body.classList.remove('device-pc', 'device-tablet', 'device-mobile');
      // 현재 디바이스 모드 클래스 추가
      document.body.classList.add(`device-${viewMode}`);
      
      // iframe 내부 문서에도 적용 (있는 경우)
      try {
        if (iframeRef.current && iframeRef.current.contentDocument) {
          const iframeBody = iframeRef.current.contentDocument.body;
          iframeBody.classList.remove('device-pc', 'device-tablet', 'device-mobile');
          iframeBody.classList.add(`device-${viewMode}`);
        }
      } catch (e) {
        console.error('iframe 접근 에러:', e);
      }
    };
    
    applyDeviceClass();
    
    return () => {
      // 컴포넌트 언마운트 시 클래스 제거
      document.body.classList.remove('device-pc', 'device-tablet', 'device-mobile');
    };
  }, [viewMode]);

  
  // 에디터 데이터 생성 - Preview.tsx와 유사하게 구현
  const editorData = {
    components,
    layoutInfo,
    selectedComponentId,
    menus,
    viewMode // viewMode를 에디터 데이터에 포함시켜 레이아웃에 전달
  };

  // layoutProps에 editorData 추가
  const enhancedLayoutProps = {
    ...layoutProps,
    editorData: editorData,
    viewMode: viewMode, // viewMode 직접 전달
    // 실제 미디어 쿼리 동작을 시뮬레이션하기 위한 속성 추가
    isMobile: viewMode === 'mobile',
    isTablet: viewMode === 'tablet',
    isDesktop: viewMode === 'pc',
    responsiveMode: true,
    // 모바일 메뉴 이벤트 핸들러 추가
    mobileMenuHandlers: {
      toggleMobileMenu: () => {
        const offcanvasMenu = document.querySelector('#offcanvas-mobile-menu');
        if (offcanvasMenu) {
          offcanvasMenu.classList.toggle('active');
        }
      },
      closeMobileMenu: () => {
        const offcanvasMenu = document.querySelector('#offcanvas-mobile-menu');
        if (offcanvasMenu) {
          offcanvasMenu.classList.remove('active');
        }
      }
    }
  };

  // 최상위 컴포넌트만 필터링 (부모가 없는 컴포넌트)
  // 활성 메뉴가 있으면 해당 메뉴에 할당된 컴포넌트만 표시
  const rootComponents = components.filter(comp => {
    // 부모가 없는 컴포넌트
    const isRoot = !comp.parentId;
    
    // 메뉴 필터링: 활성 메뉴가 없으면 모든 컴포넌트 표시
    if (!activeMenu) return isRoot;
    
    // 활성 메뉴에 할당된 컴포넌트만 표시
    return isRoot && activeMenu.components && activeMenu.components.includes(comp.id);
  });

  // 자동 스크롤 시작 함수
  const startAutoScroll = (direction) => {
    if (scrollIntervalRef.current) return; // 이미 스크롤 중이면 무시

    setIsScrolling(true);
    setScrollDirection(direction);

    scrollIntervalRef.current = setInterval(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      if (direction === 'up') {
        canvas.scrollTop -= scrollSpeed;
      } else if (direction === 'down') {
        canvas.scrollTop += scrollSpeed;
      }
    }, 16); // 약 60fps
  };

  // 자동 스크롤 중지 함수
  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    setIsScrolling(false);
    setScrollDirection(null);
  };

  // 빈 캔버스 영역 클릭 핸들러
  const handleCanvasClick = (e) => {
    // className이 문자열인지 객체인지 확인하고 그에 따라 처리
    // 이벤트가 캔버스 자체에서 발생했는지 확인 (버블링된 이벤트가 아닌지)
    const isTargetCanvasOrMainContent = e.target === e.currentTarget || 
      e.target.className === 'layout-main-content';
    
    // className이 문자열인 경우에만 includes 메서드 사용
    const hasEditorCanvasClass = typeof e.target.className === 'string' && 
      e.target.className.includes('editor-canvas');
    
    if (isTargetCanvasOrMainContent || hasEditorCanvasClass) {
      // 모든 컴포넌트 선택 해제 (selectComponent에 null 전달)
      dispatch(selectComponent(null));
    }
  };

  // 화면 모드 변경 핸들러
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    
    // 화면 모드 변경 시 메타태그 업데이트 - 실제 미리보기 문서에 반응형 메타태그 추가
    updateViewportMeta(mode);
    
    // 화면 크기 변경 이벤트 트리거
    triggerResizeEvent(viewportWidths[mode]);
    
    // DOM에 화면 모드 클래스 적용
    document.body.classList.remove('device-pc', 'device-tablet', 'device-mobile');
    document.body.classList.add(`device-${mode}`);
    
    // 모바일/태블릿 모드에서는 iframe 프리뷰 활성화
    if (mode === 'mobile' || mode === 'tablet') {
      // 현재 상태를 localStorage에 저장
      const previewData = {
        components,
        menus,
        layoutInfo: {
          selectedLayout,
          layoutProps
        }
      };
      localStorage.setItem('previewData', JSON.stringify(previewData));
      
      // iframe 설정 및 표시
      if (iframeRef.current) {
        const currentPath = activeMenu ? activeMenu.path : '/';
        // iframe 소스 설정 - 타임스탬프로 캐시 방지
        const previewUrl = `/preview?device=${mode}&path=${encodeURIComponent(currentPath)}&t=${Date.now()}`;
        
        // iframe 소스 설정 및 표시
        iframeRef.current.src = previewUrl;
        
        // timeout으로 iframe 로드 대기 - 너무 빨리 표시하면 깜빡임 발생
        setTimeout(() => {
          iframeRef.current.style.display = 'block';
          
          // 실제 컨텐츠 영역 숨기기
          const layoutMainContent = document.querySelector('.layout-main-content');
          if (layoutMainContent) {
            layoutMainContent.style.display = 'none';
          }
          
          // iframe 내부에도 이벤트 리스너 추가하여 클릭 이벤트 가로채기
          try {
            if (iframeRef.current.contentDocument) {
              iframeRef.current.contentDocument.addEventListener('click', function(e) {
                // 클릭 가능한 요소 클릭 시에만 토스트 메시지 표시
                const isInteractive = 
                  e.target.closest('a') || 
                  e.target.closest('button') || 
                  e.target.tagName === 'A' || 
                  e.target.tagName === 'BUTTON' ||
                  e.target.closest('input[type="submit"]') ||
                  e.target.closest('input[type="button"]') ||
                  e.target.closest('.cart-wrap') ||
                  e.target.closest('.hamburger-menu') ||
                  e.target.closest('.product-item') ||
                  e.target.closest('.product-card') ||
                  e.target.closest('[role="button"]') ||
                  e.target.closest('.clickable');
                
                if (isInteractive) {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  window.parent.postMessage({
                    type: 'SHOW_TOAST',
                    message: '에디터 내에서 페이지 이동 및 버튼은 사용할 수 없습니다.'
                  }, '*');
                  
                  return false;
                }
              }, true);
              
              // iframe 내부의 요소에 포인터 이벤트 스타일 적용
              const iframeStyle = iframeRef.current.contentDocument.createElement('style');
              iframeStyle.textContent = `
                /* 스크롤은 가능하도록 설정 */
                html, body { 
                  pointer-events: auto !important; 
                  overflow-y: auto !important;
                }
              `;
              iframeRef.current.contentDocument.head.appendChild(iframeStyle);
            }
          } catch (e) {
            console.error('iframe 내부 이벤트 제어 에러:', e);
          }
        }, 100);
      }
    } else {
      // PC 모드에서는 iframe 숨기고 일반 뷰 표시
      if (iframeRef.current) {
        iframeRef.current.style.display = 'none';
      }
      
      // 실제 컨텐츠 영역 다시 표시
      const layoutMainContent = document.querySelector('.layout-main-content');
      if (layoutMainContent) {
        layoutMainContent.style.display = 'flex';
      }
    }
    
    // 실제 모바일/태블릿 화면으로 전환
    if (mode === 'mobile') {
      // 모바일 메뉴 초기화 및 활성화
      initMobileMenu();
      
      // 모바일 레이아웃 클래스 추가
      const layoutContainer = document.querySelector('.layout-container');
      if (layoutContainer) {
        layoutContainer.classList.add('mobile-view-active');
      }
      
      // 모바일 CSS 미디어 쿼리 강제 적용
      forceMobileMediaQueries();
    } else if (mode === 'tablet') {
      // 태블릿 레이아웃 클래스 추가
      const layoutContainer = document.querySelector('.layout-container');
      if (layoutContainer) {
        layoutContainer.classList.remove('mobile-view-active');
        layoutContainer.classList.add('tablet-view-active');
      }
      
      // 태블릿 CSS 미디어 쿼리 강제 적용
      forceTabletMediaQueries();
    } else {
      // PC 모드로 복원
      const layoutContainer = document.querySelector('.layout-container');
      if (layoutContainer) {
        layoutContainer.classList.remove('mobile-view-active', 'tablet-view-active');
      }
      
      // PC CSS 미디어 쿼리 복원
      resetMediaQueries();
    }
  };
  
  // 뷰포트 메타 태그 업데이트 함수
  const updateViewportMeta = (mode) => {
    // 이미 존재하는 viewport 메타 태그를 찾음
    let meta = document.querySelector('meta[name="viewport"]');
    
    // 없으면 새로 생성
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'viewport';
      document.head.appendChild(meta);
    }
    
    // 모드에 따라 viewport 내용 설정
    switch (mode) {
      case 'mobile':
        meta.content = 'width=390, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'; // iPhone 12 Pro 기준
        break;
      case 'tablet':
        meta.content = 'width=820, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'; // iPad Air 기준
        break;
      case 'pc':
      default:
        meta.content = 'width=device-width, initial-scale=1.0, viewport-fit=cover';
        break;
    }
    
    // iframe 내부의 viewport 메타 태그도 업데이트
    try {
      if (iframeRef.current && iframeRef.current.contentDocument) {
        const iframeMeta = iframeRef.current.contentDocument.querySelector('meta[name="viewport"]');
        if (iframeMeta) {
          iframeMeta.content = meta.content;
        } else {
          const newMeta = iframeRef.current.contentDocument.createElement('meta');
          newMeta.name = 'viewport';
          newMeta.content = meta.content;
          iframeRef.current.contentDocument.head.appendChild(newMeta);
        }
      }
    } catch (e) {
      console.error('iframe 메타 태그 업데이트 에러:', e);
    }
    
    // 텍스트 렌더링 최적화를 위한 meta 태그 추가
    let textSizeAdjustMeta = document.querySelector('meta[name="text-size-adjust"]');
    if (!textSizeAdjustMeta) {
      textSizeAdjustMeta = document.createElement('meta');
      textSizeAdjustMeta.name = 'text-size-adjust';
      document.head.appendChild(textSizeAdjustMeta);
    }
    
    // 모바일 모드에서는 텍스트 자동 크기 조정 방지
    if (mode === 'mobile' || mode === 'tablet') {
      textSizeAdjustMeta.content = '100%';
    } else {
      textSizeAdjustMeta.content = 'auto';
    }
  };
  
  // 리사이즈 이벤트 트리거 함수
  const triggerResizeEvent = (width) => {
    // 기본 창 리사이즈 이벤트 발생
    const resizeEvent = new Event('resize');
    window.innerWidth = width; // 임시로 window.innerWidth 값을 변경
    window.dispatchEvent(resizeEvent);
    
    // iframe 내부에도 resize 이벤트 발생
    try {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        const iframeResizeEvent = new Event('resize');
        iframeRef.current.contentWindow.innerWidth = width;
        iframeRef.current.contentWindow.dispatchEvent(iframeResizeEvent);
      }
    } catch (e) {
      console.error('iframe resize 이벤트 에러:', e);
    }
  };

  // 컴포넌트의 최대 Y 위치를 계산하여 메인 영역 높이 결정
  const calculateMainContentHeight = () => {
    if (rootComponents.length === 0) return 300; // 기본 최소 높이

    let maxY = 0;
    rootComponents.forEach(comp => {
      const bottomY = comp.position.y + comp.size.height;
      if (bottomY > maxY) {
        maxY = bottomY;
      }
    });

    return Math.max(300, maxY + 50); // 최소 300px, 컴포넌트가 있으면 가장 낮은 지점 + 50px 여백
  };

  const mainContentHeight = calculateMainContentHeight();

  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'COMPONENT',
    hover: (item, monitor) => {
      if (!item.id) return; // 새 컴포넌트는 처리하지 않음
      
      const offset = monitor.getClientOffset();
      if (!offset) return;
      
      // 드래그 중임을 표시
      setIsDraggingComponent(true);
      
      // 현재 마우스 위치에서 컴포넌트 찾기
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const canvasRect = canvas.getBoundingClientRect();
      const y = offset.y - canvasRect.top;
      const x = offset.x - canvasRect.left;
      
      // 스크롤 처리
      const canvasHeight = canvasRect.height;
      
      // 위쪽 경계 근처에서 스크롤 시작
      if (y < scrollThreshold) {
        startAutoScroll('up');
      }
      // 아래쪽 경계 근처에서 스크롤 시작
      else if (y > canvasHeight - scrollThreshold) {
        startAutoScroll('down');
      }
      // 경계 영역 밖이면 스크롤 중지
      else {
        stopAutoScroll();
      }
      
      // 드래그 중인 컴포넌트의 rect 가져오기
      const draggedEl = document.getElementById(`component-${item.id}`);
      if (!draggedEl) return;
      const draggedRect = draggedEl.getBoundingClientRect();
      
      // 겹침이 가장 큰 컴포넌트 찾기 (50% 기준)
      const overlap = findMostOverlappedComponent(rootComponents, draggedRect, item.id, 0.5);
      
      if (overlap) {
        // 50% 이상 겹치는 컴포넌트가 있는 경우
        setDragOverIndex(overlap.component.order || 0);
        setDragPosition(overlap.position);
        
        // 디버깅용 로그
      } else {
        // 충분한 겹침이 없는 경우 가장 가까운 컴포넌트 찾기
        const closestComponent = findClosestComponent(rootComponents, x, y, item.id);
        if (closestComponent) {
          setDragOverIndex(closestComponent.component.order || 0);
          setDragPosition(closestComponent.position);
        } else {
          setDragOverIndex(null);
          setDragPosition(null);
        }
      }
    },
    drop: (item, monitor) => {
      // 이미 다른 곳에서 드롭된 경우 처리하지 않음
      if (monitor.didDrop()) {
        return;
      }
      
      // 드래그 종료로 표시
      setIsDraggingComponent(false);
      
      // 자동 스크롤 중지
      stopAutoScroll();
      
      
      const offset = monitor.getClientOffset();
      if (!offset) return; 

      const canvas = canvasRef.current;
      if (!canvas) return; 

      const canvasRect = canvas.getBoundingClientRect();
      
      // 캔버스 좌표로 변환
      let x = offset.x - canvasRect.left;
      let y = offset.y - canvasRect.top;
      
      // 레이아웃 사용 시 메인 컨텐츠 영역 내에 배치되도록 제한
      if (LayoutComponent) {
        const mainContent = document.querySelector('.layout-main-content');
        if (mainContent) {
          const mainContentRect = mainContent.getBoundingClientRect();
          // 메인 컨텐츠 영역 내 상대적 위치로 변환
          x = offset.x - mainContentRect.left;
          y = offset.y - mainContentRect.top;
        }
      }

      // 이미 존재하는 컴포넌트의 경우 위치 업데이트 및 순서 변경
      if (item.id) {
        // 드래그 중인 컴포넌트의 rect 가져오기
        const draggedEl = document.getElementById(`component-${item.id}`);
        if (!draggedEl) return;
        const draggedRect = draggedEl.getBoundingClientRect();
        
        // 겹침이 가장 큰 컴포넌트 찾기 (50% 기준)
        // x, y 좌표도 로깅하여 디버깅에 활용
        const overlap = findMostOverlappedComponent(rootComponents, draggedRect, item.id, 0.5);
        
        if (overlap) {
          // 충분한 겹침이 있는 경우 처리
          const { component: targetComponent, position } = overlap;
          
          // 현재 드래그 중인 컴포넌트
          const draggedComponent = rootComponents.find(comp => comp.id === item.id);
          if (!draggedComponent) return;
          
          // 현재 순서
          const currentOrder = draggedComponent.order || 0;
          const targetOrder = targetComponent.order || 0;
          
          // 새로운 순서 계산
          let newOrder = targetOrder;
          
          // 위치에 따라 순서 조정
          if (position === 'below' || position === 'right') {
            newOrder = targetOrder + 1;
          } else if (position === 'above' || position === 'left') {
            newOrder = targetOrder;
          }
          
          
          
          // 다른 컴포넌트들의 순서 조정
          rootComponents.forEach(comp => {
            if (comp.id === item.id) return;
            
            const compOrder = comp.order || 0;
            
            if (currentOrder < newOrder) {
              // 위에서 아래로 이동: 중간에 있는 컴포넌트들의 순서를 한칸씩 위로
              if (compOrder > currentOrder && compOrder <= newOrder) {
                dispatch(updateComponentPosition({
                  id: comp.id,
                  newPosition: comp.position,
                  order: compOrder - 1
                }));
              }
            } else if (currentOrder > newOrder) {
              // 아래에서 위로 이동: 중간에 있는 컴포넌트들의 순서를 한칸씩 아래로
              if (compOrder >= newOrder && compOrder < currentOrder) {
                dispatch(updateComponentPosition({
                  id: comp.id,
                  newPosition: comp.position,
                  order: compOrder + 1
                }));
                
                // 디버깅을 위한 로그
                
              }
            }
          });
          
          // 드래그 중인 컴포넌트를 새 위치로 이동
          dispatch(updateComponentPosition({
            id: item.id,
            newPosition: { x: 0, y: 0 },
            order: newOrder
          }));
        } else if (dragOverIndex !== null && dragPosition !== null) {
          // 겹침은 50% 미만이지만 가까운 컴포넌트가 있는 경우
          // 기존의 가장 가까운 컴포넌트 로직 사용
          const targetComponent = rootComponents.find(comp => (comp.order || 0) === dragOverIndex);
          if (!targetComponent) return;
          
          // 현재 드래그 중인 컴포넌트
          const draggedComponent = rootComponents.find(comp => comp.id === item.id);
          if (!draggedComponent) return;
          
          // 현재 순서
          const currentOrder = draggedComponent.order || 0;
          
          // 새로운 순서 계산
          let newOrder = dragOverIndex;
          
          // 위치에 따라 순서 조정
          if (dragPosition === 'below' || dragPosition === 'right') {
            newOrder = dragOverIndex + 1;
          } else if (dragPosition === 'above' || dragPosition === 'left') {
            newOrder = dragOverIndex;
          }
          
          
          
          // 다른 컴포넌트들의 순서 조정
          rootComponents.forEach(comp => {
            if (comp.id === item.id) return;
            
            const compOrder = comp.order || 0;
            
            if (currentOrder < newOrder) {
              // 위에서 아래로 이동: 중간에 있는 컴포넌트들의 순서를 한칸씩 위로
              if (compOrder > currentOrder && compOrder <= newOrder) {
                dispatch(updateComponentPosition({
                  id: comp.id,
                  newPosition: comp.position,
                  order: compOrder - 1
                }));
              }
            } else if (currentOrder > newOrder) {
              // 아래에서 위로 이동: 중간에 있는 컴포넌트들의 순서를 한칸씩 아래로
              if (compOrder >= newOrder && compOrder < currentOrder) {
                dispatch(updateComponentPosition({
                  id: comp.id,
                  newPosition: comp.position,
                  order: compOrder + 1
                }));
                
                // 디버깅을 위한 로그
                
              }
            }
          });
          
          // 드래그 중인 컴포넌트를 새 위치로 이동
          dispatch(updateComponentPosition({
            id: item.id,
            newPosition: { x: 0, y: 0 },
            order: newOrder
          }));
        } else {
          // 겹치는 컴포넌트가 없고 가까운 컴포넌트도 없는 경우
          // 원래 위치로 복귀
          // 드래그 중인 컴포넌트 찾기
          const draggedComponent = rootComponents.find(comp => comp.id === item.id);
          if (!draggedComponent) return;
          
          dispatch(updateComponentPosition({ 
            id: item.id, 
            newPosition: { x: 0, y: 0 },
            order: draggedComponent.order || 0
          }));
        }
      } else {
        // 새 컴포넌트 추가
        let defaultWidth = '100%';
        let defaultHeight = 50;
        
        // 정확한 컴포넌트 타입 결정
        const newCompType = item.type;
        
        switch(newCompType) {
          case COMPONENT_TYPES.TEXT:
            defaultHeight = 50;
            break;
          case COMPONENT_TYPES.IMAGE:
            defaultHeight = 200;
            break;
          case COMPONENT_TYPES.BUTTON:
            defaultHeight = 40;
            break;
          case COMPONENT_TYPES.CONTAINER:
            defaultHeight = 300;
            break;
          case COMPONENT_TYPES.ROW:
            defaultHeight = 100;
            break;
          case COMPONENT_TYPES.COLUMN:
            defaultHeight = 200;
            break;
          case COMPONENT_TYPES.LOGIN:
            defaultHeight = 400;
            break;
          case COMPONENT_TYPES.BOARD:
            defaultHeight = 600;
            break;
          case COMPONENT_TYPES.DETAIL_PAGE:
            defaultHeight = 800;
            break;
          case COMPONENT_TYPES.PRODUCT_LIST:
            defaultHeight = 100;
            break;
          case COMPONENT_TYPES.PRODUCT_DETAIL:
            defaultHeight = 200;
            break;
          case COMPONENT_TYPES.COMPANY_HISTORY:
            defaultHeight = 300;
            break;
          default:
            console.warn('알 수 없는 컴포넌트 타입:', newCompType);
            defaultHeight = 50;
        }

        // 새 고유 ID 생성
        const newComponentId = uuidv4();
        
        // 새 컴포넌트 추가
        dispatch(addComponent({
          id: newComponentId,
          type: newCompType, // 명확한 타입 사용
          position: { x: 0, y }, // x를 0으로 설정하여 왼쪽부터 시작하도록 함
          size: { width: defaultWidth, height: defaultHeight },
          style: {},
          content: getDefaultContent(newCompType),
          componentProps: newCompType === COMPONENT_TYPES.PRODUCT_LIST 
            ? { itemsPerRow: 4 } // 명시적으로 숫자 타입으로 설정
            : newCompType === COMPONENT_TYPES.PRODUCT_DETAIL
              ? { 
                  productId: 49,
                  showDescriptionTab: true,
                  showReviewsTab: true,
                  showSpecificationTab: true
                }
              : {},
          parentId: null, // 최상위 컴포넌트
          order: findInsertPosition(rootComponents, y) // 순서 정보 추가
        }));
        
        // 활성 메뉴가 있는 경우, 새 컴포넌트를 해당 메뉴에 할당
        if (activeMenu) {
          const menuComponents = [...(activeMenu.components || []), newComponentId];
          dispatch(assignComponentsToMenu({
            menuId: activeMenu.id,
            componentIds: menuComponents
          }));
        }
        
        // 컴포넌트 자동 선택 추가
        dispatch(selectComponent(newComponentId));
      }
      
      // 드롭 후 상태 초기화
      setDragOverIndex(null);
      setDragPosition(null);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver({ shallow: true }),
    }),
  }), [rootComponents, activeMenu]);

  // 드래그 종료 시 스크롤 및 상태 초기화
  useEffect(() => {
    if (!isOver) {
      setIsDraggingComponent(false);
      stopAutoScroll();
      setDragOverIndex(null);
      setDragPosition(null);
    }
    
    // 컴포넌트 언마운트 시 스크롤 정리
    return () => {
      stopAutoScroll();
    };
  }, [isOver]);

  const getDefaultContent = (type) => {
    // COMPONENT_TYPES와 직접 비교
    switch(type) {
      case COMPONENT_TYPES.TEXT:
        return '텍스트를 입력하세요';
      case COMPONENT_TYPES.IMAGE:
        return { src: 'https://via.placeholder.com/150', alt: '이미지' };
      case COMPONENT_TYPES.BUTTON:
        return '버튼';
      case COMPONENT_TYPES.LOGIN:
        return '';
      case COMPONENT_TYPES.BOARD:
        return '';
      case COMPONENT_TYPES.DETAIL_PAGE:
        return '';
      case COMPONENT_TYPES.ROW:
        return '';
      case COMPONENT_TYPES.COLUMN:
        return '';
      case COMPONENT_TYPES.CONTAINER:
        return '';
      case COMPONENT_TYPES.COMPANY_HISTORY:
        return {
          title: '회사 연혁',
          subtitle: '저희 회사의 성장 과정을 소개합니다',
          timelineStyle: 'vertical',
          colorScheme: {
            primary: '#3498db',
            secondary: '#f1c40f',
            background: '#ffffff',
            text: '#333333'
          },
          animation: true,
          historyItems: [
            {
              id: uuidv4(),
              year: '2010',
              title: '회사 설립',
              description: '혁신적인 비전을 가지고 회사가 설립되었습니다.'
            },
            {
              id: uuidv4(),
              year: '2015',
              title: '해외 시장 진출',
              description: '글로벌 시장으로 확장하여 아시아 지역에 첫 해외 지사를 설립했습니다.'
            },
            {
              id: uuidv4(),
              year: '2020',
              title: '기술 혁신상 수상',
              description: '혁신적인 기술력을 인정받아 국내 기술 혁신상을 수상했습니다.'
            },
            {
              id: uuidv4(),
              year: '2023',
              title: '신규 서비스 론칭',
              description: '고객 중심의 혁신적인 서비스를 성공적으로 출시했습니다.'
            }
          ]
        };
      default:
        console.warn('알 수 없는 컴포넌트 타입(컨텐츠 생성):', type);
        return '';
    }
  };

  // 순서 기반으로 삽입 위치 찾기
  const findInsertPosition = (components, dropY) => {
    if (!components || components.length === 0) return 0;
    
    // y 좌표로 정렬된 컴포넌트 목록
    const sortedComponents = [...components].sort((a, b) => a.position.y - b.position.y);
    
    // 드롭 위치보다 아래에 있는 첫 번째 컴포넌트 찾기
    const insertIndex = sortedComponents.findIndex(comp => comp.position.y > dropY);
    
    // 찾지 못했다면 맨 뒤에 추가
    return insertIndex === -1 ? components.length : insertIndex;
  };

  // 두 사각형 간의 겹침 영역 계산
  const calculateOverlap = (rect1, rect2) => {
    // 겹침 영역 계산
    const xOverlap = Math.max(0, Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left));
    const yOverlap = Math.max(0, Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top));
    
    const intersectArea = xOverlap * yOverlap;
    const rect1Area = (rect1.right - rect1.left) * (rect1.bottom - rect1.top);
    
    // 겹침 비율 계산 (작은 사각형 기준)
    const overlapRatio = intersectArea / rect1Area;
    
    // 가장 큰 겹침 방향 결정
    const center1X = (rect1.left + rect1.right) / 2;
    const center1Y = (rect1.top + rect1.bottom) / 2;
    const center2X = (rect2.left + rect2.right) / 2;
    const center2Y = (rect2.top + rect2.bottom) / 2;

    // 수평/수직 중 어느쪽에 더 큰 겹침이 있는지 계산
    const horizontalCenter = Math.abs(center1X - center2X) < (rect1.right - rect1.left + rect2.right - rect2.left) / 4;
    const verticalCenter = Math.abs(center1Y - center2Y) < (rect1.bottom - rect1.top + rect2.bottom - rect2.top) / 4;

    let position = null;

    // 위쪽으로의 이동을 더 잘 감지하기 위해 수직 방향 비교에 가중치 부여
    const verticalDiff = center1Y - center2Y;
    const horizontalDiff = center1X - center2X;
    
    // 수직 방향에 더 가중치를 둠 (위/아래 방향 우선)
    if (Math.abs(verticalDiff) > Math.abs(horizontalDiff) * 0.5) {
      // 수직 방향이 더 중요한 경우
      if (verticalDiff < 0) {
        position = 'above'; // rect1이 rect2 위에 있음
      } else {
        position = 'below'; // rect1이 rect2 아래에 있음
      }
    } else {
      // 수평 방향이 더 중요한 경우
      if (horizontalDiff < 0) {
        position = 'left';
      } else {
        position = 'right';
      }
    }

   

    return {
      intersectArea,
      overlapRatio,
      position
    };
  };

  // 가장 큰 겹침이 있는 컴포넌트 찾기
  const findMostOverlappedComponent = (components, dragRect, excludeId, threshold = 0.5) => {
    if (!components || components.length === 0) return null;
    
    let maxOverlap = 0;
    let bestMatch = null;
    let bestPosition = null;

    // 위쪽 컴포넌트를 더 민감하게 감지하도록 위쪽 방향의 임계값을 약간 낮춤
    const topThreshold = threshold * 0.8; // 위쪽 방향은 약간 더 민감하게 설정

    for (const component of components) {
      if (component.id === excludeId) continue;
      
      const componentEl = document.getElementById(`component-${component.id}`);
      if (!componentEl) continue;
      
      const targetRect = componentEl.getBoundingClientRect();
      const { overlapRatio, position } = calculateOverlap(dragRect, targetRect);
      
      // 위쪽 방향인 경우 임계값을 더 낮게 적용
      const effectiveThreshold = position === 'above' ? topThreshold : threshold;
      
      // 겹침 비율이 임계값보다 크고 현재까지의 최대 겹침보다 크면 업데이트
      if (overlapRatio > effectiveThreshold && overlapRatio > maxOverlap) {
        maxOverlap = overlapRatio;
        bestMatch = component;
        bestPosition = position;
      }
    }
    
    return bestMatch ? { 
      component: bestMatch, 
      position: bestPosition,
      overlapRatio: maxOverlap
    } : null;
  };

  // 가장 가까운 컴포넌트와 위치 찾기
  const findClosestComponent = (components, x, y, excludeId) => {
    if (!components || components.length === 0) return null;
    
    let closestDistance = Infinity;
    let closestComponent = null;
    let position = null;
    
    // 위쪽 방향 감지를 더 민감하게 설정 (거리에 가중치 적용)
    const topWeight = 0.8; // 위쪽 방향에 20% 더 가중치 부여
    
    for (const component of components) {
      if (component.id === excludeId) continue;
      
      const componentEl = document.getElementById(`component-${component.id}`);
      if (!componentEl) continue;
      
      const rect = componentEl.getBoundingClientRect();
      
      // 각 방향으로의 거리 계산
      let topDistance = Math.abs(y - rect.top);
      const bottomDistance = Math.abs(y - rect.bottom);
      const leftDistance = Math.abs(x - rect.left);
      const rightDistance = Math.abs(x - rect.right);
      
      // 위쪽 방향 거리에 가중치 적용 (더 가깝게 인식)
      topDistance = topDistance * topWeight;
      
      // 가장 가까운 방향 찾기
      let minDistance = Math.min(topDistance, bottomDistance, leftDistance, rightDistance);
      let closestPosition;
      
      if (minDistance === topDistance) {
        closestPosition = 'above';
      } else if (minDistance === bottomDistance) {
        closestPosition = 'below';
      } else if (minDistance === leftDistance) {
        closestPosition = 'left';
      } else {
        closestPosition = 'right';
      }
      
      // 수직 방향(위/아래)에 더 가중치 부여
      if (closestPosition === 'left' || closestPosition === 'right') {
        // 수평 방향인 경우 가중치 적용 (약간 멀게 인식)
        minDistance = minDistance * 1.2;
      }
      
      if (minDistance < closestDistance) {
        closestDistance = minDistance;
        closestComponent = component;
        position = closestPosition;
      }
    }
    
    return closestComponent ? { 
      component: closestComponent, 
      position,
      index: closestComponent.order || 0,
      distance: closestDistance
    } : null;
  };

  // 드래그 위치에 따른 유효한 드롭 가이드라인 생성
  const renderDropGuides = (component, index) => {
    const order = component.order || index;
    const isTargetComponent = dragOverIndex === order;
    const showAboveLine = isTargetComponent && dragPosition === 'above';
    const showBelowLine = isTargetComponent && dragPosition === 'below';
    const showLeftLine = isTargetComponent && dragPosition === 'left';
    const showRightLine = isTargetComponent && dragPosition === 'right';
    
    // 겹침 방향에 따른 스타일 클래스 지정
    const overlapClass = isTargetComponent ? `overlap-target overlap-${dragPosition}` : '';
    
    return (
      <div 
        key={component.id} 
        style={{ position: 'relative' }}
        className={isDraggingComponent ? overlapClass : ''}
      >
        {showAboveLine && (
          <div 
            className="drop-indicator-line drop-indicator-horizontal" 
            style={{
              top: '-2px',
              transform: 'translateY(-50%)'
            }} 
          />
        )}
        
        {showBelowLine && (
          <div 
            className="drop-indicator-line drop-indicator-horizontal" 
            style={{
              bottom: '-2px',
              transform: 'translateY(50%)'
            }} 
          />
        )}
        
        {showLeftLine && (
          <div 
            className="drop-indicator-line drop-indicator-vertical" 
            style={{
              left: '-2px',
              transform: 'translateX(-50%)'
            }} 
          />
        )}
        
        {showRightLine && (
          <div 
            className="drop-indicator-line drop-indicator-vertical" 
            style={{
              right: '-2px',
              transform: 'translateX(50%)'
            }} 
          />
        )}
        
        <ComponentRenderer component={component} />
      </div>
    );
  };

  // 프리뷰 창 참조 제거 - 더 이상 필요 없음
  
  // 별도 프리뷰 창 열기 함수 제거 - 더 이상 필요 없음
  
  // 화면 모드 선택 버튼 렌더링 함수
  const renderViewModeSelector = () => {
    return (
      <div className="device-view-selector">
        <div className="device-buttons">
          <button 
            className={viewMode === 'pc' ? 'active' : ''} 
            onClick={() => handleViewModeChange('pc')}
            title="PC 화면"
          >
            <i className="fa fa-desktop"></i>
            <span>PC</span>
          </button>
          <button 
            className={viewMode === 'tablet' ? 'active' : ''} 
            onClick={() => handleViewModeChange('tablet')}
            title="태블릿 화면"
          >
            <i className="fa fa-tablet"></i>
            <span>태블릿</span>
          </button>
          <button 
            className={viewMode === 'mobile' ? 'active' : ''} 
            onClick={() => handleViewModeChange('mobile')}
            title="모바일 화면"
          >
            <i className="fa fa-mobile"></i>
            <span>모바일</span>
          </button>
        </div>
      </div>
    );
  };

  // 현재 활성화된 메뉴 정보 표시
  const renderCurrentPath = () => {
    if (!activeMenu) return null;
    
    return (
      <div className="current-path">
        <span className="path-label">현재 경로:</span>
        <span className="path-value">{activeMenu.path}</span>
      </div>
    );
  };

  // 키보드 이벤트 핸들러 - 백스페이스나 Delete 키로 선택된 컴포넌트 삭제
  useEffect(() => {
    const handleKeyDown = (e) => {
      // 선택된 컴포넌트가 있고, (Shift+백스페이스나 Shift+Delete 키를 누른 경우)
      if (selectedComponentId && ((e.key === 'Backspace' && e.shiftKey) || (e.key === 'Delete' && e.shiftKey))) {
        // 입력 필드에서 키 입력 중이 아닌 경우에만 처리 (input, textarea 등에서는 동작하지 않도록)
        if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) {
          e.preventDefault(); // 기본 동작 방지
          
          // 삭제 확인 창 표시
          const isConfirmed = window.confirm("선택한 컴포넌트를 삭제하시겠습니까?");
          
          // 확인 시 삭제 실행
          if (isConfirmed) {
            dispatch(removeComponent(selectedComponentId));
          }
        }
      }
    };

    // 이벤트 리스너 등록
    window.addEventListener('keydown', handleKeyDown);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedComponentId, dispatch]);

  // 모바일 디바이스 표시기 렌더링 함수 추가
  const renderDeviceFrame = () => {
    // 모바일/태블릿 환경이 아닌 경우 프레임 표시 안함
    if (viewMode === 'pc') return null;
    
    // 모바일/태블릿 경우 프레임만 추가
    const isMobile = viewMode === 'mobile';
    
    return (
      <div 
        className={`device-frame device-frame-${viewMode}`}
        style={{
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 5,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start'
        }}
      >
        {/* 모바일 상단 노치 */}
        {isMobile && (
          <div
            style={{
              position: 'absolute',
              top: '0',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '140px',
              height: '24px',
              background: '#111',
              borderRadius: '0 0 15px 15px',
              zIndex: 6
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '8px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '6px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '3px'
              }}
            />
          </div>
        )}
        
        {/* 모바일 하단 홈 버튼 */}
        {isMobile && (
          <div
            style={{
              position: 'absolute',
              bottom: '0',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              background: 'rgba(255, 255, 255, 0.5)',
              borderRadius: '2px',
              margin: '0 auto'
            }}
          />
        )}
      </div>
    );
  };

  // 모바일 CSS 미디어 쿼리 강제 적용 함수 추가
  const forceMobileMediaQueries = () => {
    // 실제 모바일 화면에서 사용되는 CSS 클래스 강제 적용
    const offcanvasMenu = document.querySelector('#offcanvas-mobile-menu');
    const mobileMenuButton = document.querySelector('.mobile-aside-button');
    
    // 모바일 메뉴 버튼 표시 (숨겨져 있을 경우)
    if (mobileMenuButton) {
      mobileMenuButton.style.display = 'block';
    }
    
    // 레이아웃 컴포넌트에 모바일 클래스 적용
    document.querySelectorAll('.flone-navigation').forEach(nav => {
      nav.classList.add('mobile-menu');
      nav.classList.remove('desktop-menu');
    });
    
    // 모바일에서 숨겨지는 요소 처리
    document.querySelectorAll('.d-none-mobile').forEach(el => {
      el.style.display = 'none';
    });
    
    // 모바일에서만 표시되는 요소 처리
    document.querySelectorAll('.d-block-mobile').forEach(el => {
      el.style.display = 'block';
    });
    
    // 레이아웃에 따라 추가적인 모바일 스타일 적용
    applyResponsiveLayout('mobile');
    
    // 텍스트 렌더링 최적화 적용
    document.querySelectorAll('p, h1, h2, h3, h4, h5, h6, span, a').forEach(el => {
      el.style.maxWidth = '100%';
      el.style.wordBreak = 'break-word';
      el.style.overflowWrap = 'break-word';
    });
    
    // 이미지 최적화
    document.querySelectorAll('img').forEach(img => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
    });
    
    // 컨테이너 요소 최적화
    document.querySelectorAll('.container, .container-fluid, .row, [class^="col-"]').forEach(el => {
      el.style.width = '100%';
      el.style.maxWidth = '100%';
      el.style.boxSizing = 'border-box';
    });
  };

  // 태블릿 CSS 미디어 쿼리 강제 적용 함수 추가
  const forceTabletMediaQueries = () => {
    // 태블릿 화면에서 사용되는 CSS 클래스 강제 적용
    document.querySelectorAll('.d-none-tablet').forEach(el => {
      el.style.display = 'none';
    });
    
    document.querySelectorAll('.d-block-tablet').forEach(el => {
      el.style.display = 'block';
    });
    
    // 레이아웃에 따라 추가적인 태블릿 스타일 적용
    applyResponsiveLayout('tablet');
  };

  // 미디어 쿼리 리셋 함수 추가
  const resetMediaQueries = () => {
    // 모바일/태블릿 모드에서 변경한 스타일 초기화
    document.querySelectorAll('.d-none-mobile, .d-none-tablet').forEach(el => {
      el.style.display = '';
    });
    
    document.querySelectorAll('.d-block-mobile, .d-block-tablet').forEach(el => {
      el.style.display = '';
    });
    
    // 네비게이션 클래스 복원
    document.querySelectorAll('.flone-navigation').forEach(nav => {
      nav.classList.remove('mobile-menu');
      nav.classList.add('desktop-menu');
    });
    
    // 레이아웃 복원
    applyResponsiveLayout('pc');
  };

  // 반응형 레이아웃 적용 함수 추가
  const applyResponsiveLayout = (deviceType) => {
    // 현재 선택된 레이아웃에 따라 적절한 반응형 레이아웃 적용
    const layoutId = selectedLayout;
    
    if (layoutId && layoutId.startsWith('flone')) {
      // Flone 레이아웃 처리
      const header = document.querySelector('.header-area');
      const footer = document.querySelector('.footer-area');
      
      if (deviceType === 'mobile') {
        // 모바일 레이아웃 적용
        if (header) header.classList.add('mobile-header');
        if (footer) footer.classList.add('mobile-footer');
      } else if (deviceType === 'tablet') {
        // 태블릿 레이아웃 적용
        if (header) {
          header.classList.remove('mobile-header');
          header.classList.add('tablet-header');
        }
        if (footer) {
          footer.classList.remove('mobile-footer');
          footer.classList.add('tablet-footer');
        }
      } else {
        // PC 레이아웃으로 복원
        if (header) {
          header.classList.remove('mobile-header', 'tablet-header');
        }
        if (footer) {
          footer.classList.remove('mobile-footer', 'tablet-footer');
        }
      }
    }
  };

  // 모바일 메뉴 초기화 함수 개선
  const initMobileMenu = () => {
    // 모바일 메뉴 토글 버튼 기능 추가 (0.5초 지연 후 실행)
    setTimeout(() => {
      const mobileMenuButton = document.querySelector('.mobile-aside-button');
      const offcanvasMenu = document.querySelector('#offcanvas-mobile-menu');
      const closeButton = document.querySelector('.offcanvas-menu-close');
      
      if (!mobileMenuButton) {
        // 모바일 메뉴 버튼이 없으면 생성
        const headerRight = document.querySelector('.header-right');
        if (headerRight) {
          const button = document.createElement('button');
          button.className = 'mobile-aside-button';
          button.innerHTML = '<i class="pe-7s-menu"></i>';
          headerRight.prepend(button);
          
          button.addEventListener('click', function() {
            if (offcanvasMenu) {
              offcanvasMenu.classList.add('active');
            }
          });
        }
      } else {
        // 기존 버튼에 이벤트 리스너 추가
        mobileMenuButton.addEventListener('click', function() {
          if (offcanvasMenu) {
            offcanvasMenu.classList.add('active');
          }
        });
      }
      
      if (closeButton) {
        closeButton.addEventListener('click', function() {
          if (offcanvasMenu) {
            offcanvasMenu.classList.remove('active');
          }
        });
      }
    }, 500);
  };

  // 추가: 태블릿/모바일 모드에서 전역 이벤트 가로채기 (페이지 이동 방지)
  useEffect(() => {
    // 페이지 이동을 방지하는 함수
    const preventNavigation = (e) => {
      if (viewMode !== 'pc') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // a 태그의 기본 동작을 방지
    const handleAnchorClick = (e) => {
      if (viewMode !== 'pc') {
        const isPreviewContent = e.target.closest('.layout-container') || 
                                e.target.closest('.layout-main-content') || 
                                e.target.closest('.editor-content');
        
        const isEditorUI = e.target.closest('.editor-toolbar') || 
                          e.target.closest('.device-view-selector') || 
                          e.target.closest('.device-buttons') ||
                          e.target.closest('.editor-canvas-header') ||
                          e.target.closest('.property-panel') ||
                          e.target.closest('.component-library');
        
        if (isPreviewContent && !isEditorUI) {
          e.preventDefault();
          e.stopPropagation();
          showToastMessage('에디터 내에서 페이지 이동은 불가능합니다.');
          return false;
        }
      }
    };

    // 전역 이벤트 리스너 등록
    if (viewMode !== 'pc') {
      // 태블릿/모바일 모드일 때만 이벤트 리스너 등록
      document.addEventListener('submit', preventNavigation, {capture: true, passive: false});
      window.addEventListener('beforeunload', preventNavigation, {capture: true, passive: false});
      
      // 모든 앵커 태그에 이벤트 리스너 추가
      const anchors = document.querySelectorAll('a');
      anchors.forEach(anchor => {
        anchor.addEventListener('click', handleAnchorClick, {capture: true, passive: false});
      });
    }

    // 정리 함수
    return () => {
      document.removeEventListener('submit', preventNavigation, {capture: true, passive: false});
      window.removeEventListener('beforeunload', preventNavigation, {capture: true, passive: false});
      
      const anchors = document.querySelectorAll('a');
      anchors.forEach(anchor => {
        anchor.removeEventListener('click', handleAnchorClick, {capture: true, passive: false});
      });
    };
  }, [viewMode]);

  return (
    <div 
      id="editor-canvas"
      ref={(node) => {
        canvasRef.current = node;
        drop(node);
      }}  
      className={`editor-canvas ${isOver ? 'drop-active' : ''} ${isDraggingComponent ? 'dragging-over' : ''} device-${viewMode}`}
      style={{ 
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: viewMode === 'mobile' ? '900px' : (viewMode === 'tablet' ? '1230px' : '100%'), // 디바이스 프레임 + 여백
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        backgroundColor: 'transparent'
      }}
      onClick={handleCanvasClick}
    >
      {/* 격자 무늬 배경 추가 - 직접 스타일링 적용 */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: '#f5f5f5',
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          zIndex: 0,
          minHeight: viewMode === 'mobile' ? '844px' : (viewMode === 'tablet' ? '1180px' : '100%'),
          height: 'auto' // 내용에 따라 자동 확장
        }}
      />
      
      {/* 화면 모드 선택 버튼 추가 */}
      <div className="editor-canvas-header" style={{ position: 'relative', zIndex: 1 }}>
        <div className="editor-toolbar">
          {renderViewModeSelector()}
          {viewMode !== 'pc' && (
            <div className="device-edit-warning">
              태블릿/모바일 버전은 편집이 불가능합니다.
            </div>
          )}
          {renderCurrentPath()}
        </div>
      </div>
      
      {/* 토스트 메시지 추가 */}
      {showToast && (
        <div className="toast-message">
          <div className="toast-content">
            <i className="fa fa-info-circle"></i> {toastMessage}
          </div>
        </div>
      )}
      
      {/* 스크롤 안내 요소 */}
      {isDraggingComponent && (
        <>
          <div 
            className={`scroll-indicator scroll-indicator-top ${scrollDirection === 'up' ? 'active' : ''}`}
          />
          <div 
            className={`scroll-indicator scroll-indicator-bottom ${scrollDirection === 'down' ? 'active' : ''}`}
          />
        </>
      )}
      
      {LayoutComponent ? (
        <div 
          className={`layout-container device-${viewMode}`}
          style={{ 
            position: 'relative', 
            height: viewMode === 'mobile' ? 'auto' : (viewMode === 'tablet' ? 'auto' : '100%'),
            minHeight: viewMode === 'mobile' ? '844px' : (viewMode === 'tablet' ? '1180px' : '100%'),
            width: viewSizes[viewMode].width,
            maxWidth: viewSizes[viewMode].maxWidth,
            margin: '0 auto',
            transition: 'width 0.3s, max-width 0.3s',
            zIndex: 1,
            backgroundColor: 'white',
            ...deviceFrameStyles[viewMode],
            overflow: 'auto', // hidden에서 auto로 변경
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            boxSizing: 'border-box'
          }}
        >
          {renderDeviceFrame()}
          
          {/* iframe으로 레이아웃을 감싸서 실제 브라우저처럼 미디어 쿼리 적용 */}
          <iframe
            ref={iframeRef}
            title="Layout Preview"
            style={{
              border: 'none',
              width: '100%',
              height: 'auto',
              minHeight: '100%',
              display: 'none', // 초기에는 숨김, 모바일/태블릿 뷰에서 표시됨
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              backgroundColor: 'white',
              overflow: 'auto' // 내부 스크롤 허용
            }}
          />
          
          <LayoutComponent 
            {...enhancedLayoutProps}
            responsiveMode={true}
            deviceType={viewMode}
          >
            {/* 레이아웃 내부에 메인 컨텐츠 영역을 정의하고, 그 안에 컴포넌트 배치 */}
            <div 
              className={`layout-main-content device-${viewMode}`}
              style={{ 
                position: 'relative', 
                minHeight: viewMode === 'mobile' ? '300px' : '300px',
                maxHeight: viewMode === 'mobile' ? 'calc(844px - 80px)' : (viewMode === 'tablet' ? 'calc(1180px - 32px)' : 'auto'),
                padding: viewMode === 'mobile' ? '0 5px' : '0px',
                width: '100%',
                maxWidth: viewMode === 'mobile' ? '100%' : '100%',
                backgroundColor: isOver ? 'rgba(0, 123, 255, 0.05)' : 'white',
                border: isOver ? '2px dashed #007bff' : 'none',
                transition: 'width 0.3s, max-width 0.3s',
                display: 'flex',
                flexDirection: 'column',
                gap: viewMode === 'mobile' ? '10px' : '15px',
                overflow: 'auto',
                boxShadow: viewMode === 'pc' ? '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)' : 'none',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                boxSizing: 'border-box'
              }}
              onClick={handleCanvasClick}
            >
              {rootComponents.length === 0 && activeMenu ? (
                <div className="empty-menu-content" style={{ height: viewMode === 'mobile' ? 'calc(844px - 100px)' : (viewMode === 'tablet' ? 'calc(1180px - 50px)' : '300px'), overflow: 'hidden' }}>
                  <p>이 메뉴에 할당된 컴포넌트가 없습니다.</p>
                  <p>컴포넌트 라이브러리에서 요소를 드래그하여 추가하세요.</p>
                </div>
              ) : (
                rootComponents
                  .slice()
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((component, index) => (
                    <React.Fragment key={component.id}>
                      <ComponentRenderer
                        component={component}
                        isSelected={selectedComponentId === component.id}
                        viewMode={viewMode}
                      />
                      {isDraggingComponent && renderDropGuides(component, component.order || index)}
                    </React.Fragment>
                  ))
              )}
            </div>
          </LayoutComponent>
        </div>
      ) : (
        <div 
          className={`editor-content device-${viewMode}`}
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: viewMode === 'mobile' ? '10px' : '15px',
            width: viewSizes[viewMode].width,
            maxWidth: viewSizes[viewMode].maxWidth,
            margin: '0 auto',
            padding: viewMode === 'mobile' ? '0 5px' : '0px',
            transition: 'width 0.3s, max-width 0.3s',
            backgroundColor: 'white',
            overflow: 'visible',
            position: 'relative',
            ...deviceFrameStyles[viewMode],
            boxShadow: viewMode === 'pc' ? '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)' : deviceFrameStyles[viewMode].boxShadow,
            zIndex: 1,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            boxSizing: 'border-box'
          }}
        >
          {renderDeviceFrame()}
          {rootComponents.length === 0 && activeMenu ? (
            <div className="empty-menu-content" style={{ height: viewMode === 'mobile' ? 'calc(844px - 100px)' : (viewMode === 'tablet' ? 'calc(1180px - 50px)' : '300px'), overflow: 'hidden' }}>
              <p>이 메뉴에 할당된 컴포넌트가 없습니다.</p>
              <p>컴포넌트 라이브러리에서 요소를 드래그하여 추가하세요.</p>
            </div>
          ) : (
            rootComponents
              .slice()
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((component, index) => (
                <React.Fragment key={component.id}>
                  <ComponentRenderer
                    component={component}
                    isSelected={selectedComponentId === component.id}
                    viewMode={viewMode}
                  />
                  {isDraggingComponent && renderDropGuides(component, component.order || index)}
                </React.Fragment>
              ))
          )}
        </div>
      )}
    </div>
  );
}

// mergeRefs 함수 추가 - 여러 ref를 하나로 합치기 위한 유틸리티 함수
function mergeRefs(...refs) {
  return (node) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref != null) {
        ref.current = node;
      }
    });
  };
}

export default EditorCanvas;